/**
 * Regex Tester Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseRegex } from '@/lib/regex/parseRegex';

export const RegexTesterTool: ITransformerTool = {
  id: 'regex-tester',
  name: 'Regex Tester',
  description: 'Test a regex against input text and return matches',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['regex', 'test', 'match', 'groups'],

  seo: {
    slug: 'regex-tester',
    pageTitle: 'Regex Tester - Free Online Regular Expression Tool',
    metaDescription: 'Test regular expressions against input text with real-time match highlighting. View capture groups and results. Fast, private, runs in your browser.',
    keywords: ['regex tester', 'regular expression tester', 'regex match', 'regex online', 'test regex'],
    relatedTools: ['regex-railroad-visualizer'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'pattern',
      type: 'string',
      label: 'Pattern',
      defaultValue: '',
      description: 'Regex pattern or literal like /pattern/flags',
    },
    {
      name: 'flags',
      type: 'string',
      label: 'Flags',
      defaultValue: 'g',
      description: 'Regex flags (e.g. g, i, m)',
    },
    {
      name: 'maxMatches',
      type: 'number',
      label: 'Max Matches',
      defaultValue: 200,
      description: 'Safety limit on number of matches returned',
      validation: { min: 1, max: 5000, step: 50 },
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const maxMatches = typeof config.maxMatches === 'number' ? (config.maxMatches as number) : 200;
    const patternRaw = String(config.pattern ?? '').trim();

    if (!patternRaw) {
      throw ToolError.validation('Pattern is required');
    }

    const parsed = parseRegex(patternRaw, { inputFormat: 'auto', flags: String(config.flags ?? '') });
    if (!parsed.ok) {
      throw ToolError.validation(parsed.error);
    }

    const flags = parsed.flags.includes('g') ? parsed.flags : `${parsed.flags}g`;
    const re = new RegExp(parsed.pattern, flags);

    const text = String(input ?? '');
    const matches: Array<{ index: number; match: string; groups: string[]; namedGroups?: Record<string, string> }> = [];

    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      matches.push({
        index: m.index,
        match: m[0] ?? '',
        groups: m.slice(1).map((g) => (g == null ? '' : String(g))),
        namedGroups: m.groups ? { ...m.groups } : undefined,
      });

      if (matches.length >= maxMatches) break;
      // Avoid infinite loops on zero-length matches.
      if (m[0] === '') re.lastIndex += 1;
    }

    const out = {
      pattern: parsed.pattern,
      flags,
      matchCount: matches.length,
      truncated: matches.length >= maxMatches,
      matches,
    };

    return JSON.stringify(out, null, 2);
  },
};
